import SSDataTable from '@/features/common/table/SSDataTable';
import TruncatedTextComponent from '@/features/common/TruncatedTextComponent';
import { useEffect, useState, useMemo, useRef } from 'react';
import { useSearchTracersQuery } from '@/app/services/nucleus';
import SearchInput from '@/features/common/SearchInput';
import AlertComponent from '../common/AlertComponent';
import { Box, Heading, Switch, HStack, FormControl, FormLabel, Center, Button } from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { isArrayValue, isRealValue } from '@/utils/helpers.js';
import { useNotification } from '@/hooks/useNotification.js';
import { useServerSideSorting } from '@/hooks/useServerSideSorting.js';
import { usePagination } from '@/hooks/usePagination.js';

const SearchTracer = () => {
  const tableInstanceRef = useRef(null);
  const firstTimeLoaded = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchText, setSearchText] = useState('');
  const [isOnlyErrors, setIsOnlyErrors] = useState(false);
  const [pageCount, setPageCount] = useState();
  const [urlParamsParsed, setUrlParamsParsed] = useState(false);

  const {
    limit,
    onPaginationChange,
    pagination: paginationState,
    offset,
  } = usePagination({
    onPageChange: (pageState) => {
      setSearchParams((params) => {
        if (Number(params.get('page')) !== Number(pageState.pageIndex)) {
          params.delete('sel');
        }
        params.set('page', pageState.pageIndex);
        params.set('limit', pageState.pageSize);
        return params;
      });
    },
  });
  const { sorting, onSortingChange, sortByField } = useServerSideSorting([{ id: 'updated_at', desc: true }]);

  const {
    data: tracerData,
    isLoading,
    isError,
    error: errorDescription,
  } = useSearchTracersQuery(
    {
      search: searchText,
      sortBy: sortByField,
      errorCount: isOnlyErrors ? 1 : 0,
      limit,
      offset,
    },
    { skip: !urlParamsParsed }, // Skip until URL params are parsed
  );

  useNotification(isError, null, isError, 'error', 'Error fetching tracers');

  useEffect(() => {
    if (isRealValue(tracerData?.total)) {
      setPageCount(Math.ceil(tracerData?.total / limit));
    }
  }, [tracerData?.total]);

  useEffect(() => {
    if (isOnlyErrors && isArrayValue(tracerData?.items) && tracerData?.items.every((item) => item.error_count) && tableInstanceRef.current) {
      let selected = null;
      if (isRealValue(searchParams.get('sel'))) {
        selected = Number(searchParams.get('sel'));
      }
      tracerData.items.map((row, index) => {
        if (index === selected) {
          tableInstanceRef.current.options.meta?.addRowColor(index, 'red.100');
        } else {
          tableInstanceRef.current.options.meta?.addRowColor(index, 'red.50');
        }
      });
    }
  }, [isOnlyErrors, tracerData?.items, tableInstanceRef.current]);

  const handleViewTracer = (tracer, index, params) => {
    const newParams = new URLSearchParams(params);
    newParams.set('sel', index);
    setSearchParams(newParams);

    const path = `/tracers/${tracer?.trace_id}`;
    setTimeout(() => {
      navigate(path, {
        state: {
          from: {
            search: `?${newParams.toString()}`,
          },
        },
      });
    }, 50);
  };

  const onTableLoaded = (table) => {
    tableInstanceRef.current = table;
  };

  // Single effect to handle all row highlighting
  useEffect(() => {
    if (tableInstanceRef.current && tracerData?.items) {
      const selectedRowIndex = searchParams.get('sel') ? Number(searchParams.get('sel')) : null;

      tracerData.items.forEach((row, index) => {
        let color = 'white'; // default

        // Handle errors-only coloring
        if (isOnlyErrors && row.error_count > 0) {
          color = index === selectedRowIndex ? 'red.100' : 'red.50';
        }
        // Handle regular selection highlighting
        else if (index === selectedRowIndex) {
          color = 'gray.50';
        }

        tableInstanceRef.current.options.meta?.addRowColor(index, color);
      });
    }
  }, [tableInstanceRef.current, tracerData?.items, searchParams, isOnlyErrors]);

  // parse query params on page load
  useEffect(() => {
    if (searchParams && !firstTimeLoaded.current) {
      firstTimeLoaded.current = true;
      let selectedRowIndex = null;
      const pageState = { ...paginationState };

      for (let param of searchParams) {
        if (Array.isArray(param) && param.at(0) === 'search') {
          setSearchText(param.at(1));
        }
        if (Array.isArray(param) && param.at(0) === 'errors') {
          setIsOnlyErrors(param.at(1) === 'true');
        }
        if (Array.isArray(param) && param.at(0) === 'sel') {
          selectedRowIndex = Number(param.at(1));
        }
        if (Array.isArray(param) && param.at(0) === 'page') {
          pageState.pageIndex = Number(param.at(1));
        }
        if (Array.isArray(param) && param.at(0) === 'limit') {
          pageState.pageSize = Number(param.at(1));
        }
      }

      onPaginationChange(pageState);
      setUrlParamsParsed(true);
    }
  }, [location.pathname, searchParams]);

  const columnHelper = createColumnHelper();

  const tracerColumns = useMemo(
    () => [
      columnHelper.accessor('summary', {
        header: 'Summary',
        cell: (info) => {
          return <TruncatedTextComponent key={`${info.getValue()}`} title="Summary" text={info.getValue()} length={100} />;
        },
        size: 65,
        meta: { widthStrategy: '%' },
        enableSorting: false,
      }),
      columnHelper.accessor('error_count', {
        header: 'Number of Errors',
        enableSorting: false,
        size: 15,
        cell: (info) => <Center paddingRight={4}>{info.getValue()}</Center>,
        meta: { align: 'center', widthStrategy: '%' },
      }),
      columnHelper.accessor('inserted_at', {
        header: 'Inserted At',
        cell: (info) => {
          const date = new Date(info.getValue());
          return date.toLocaleString();
        },
        size: 10,
        meta: { widthStrategy: '%' },
      }),
      columnHelper.accessor('updated_at', {
        header: 'Updated At',
        cell: (info) => {
          const date = new Date(info.getValue());
          return date.toLocaleString();
        },
        size: 10,
        meta: { widthStrategy: '%' },
      }),
      columnHelper.accessor('', {
        header: 'Trace',
        cell: (info) => {
          const tracer = info.row.original;
          return (
            <HStack fontWeight="bold">
              <Button size="sm" variant="link" colorScheme={'purple'} onClick={() => handleViewTracer(tracer, info.row.index, searchParams)}>
                Trace
              </Button>
            </HStack>
          );
        },
        size: 10,
        meta: { widthStrategy: '%' },
      }),
    ],
    [searchText, searchParams],
  );

  const handleClearSearchText = () => {
    setSearchText('');
    searchParams.delete('search');
    setSearchParams(searchParams);
  };

  useEffect(() => {
    if (isRealValue(searchText) && searchText !== '') {
      const timeoutId = setTimeout(() => {
        setSearchParams((params) => {
          if (searchText !== '') {
            params.set('search', searchText);
            onPaginationChange({ pageSize: limit, pageIndex: 0 });
          } else if (searchText === '' && firstTimeLoaded.current) {
            params.delete('search');
          }
          return params;
        });
      }, 200);
      return () => clearTimeout(timeoutId);
    }
  }, [searchText]);

  const handleSearchTextChange = (event) => {
    setSearchText(event.target.value);
  };

  const handleErrorsOnlyChange = (event) => {
    const checked = event.target.checked;
    setIsOnlyErrors(checked);
    setSearchParams((params) => {
      params.delete('sel');
      params.set('errors', checked ? 'true' : 'false');
      return params;
    });
    onPaginationChange({ pageSize: limit, pageIndex: 0 });
  };

  return (
    <Box data-test-id="pact-search-tracer">
      <Heading as="h4" size="md" fontWeight="bold" color="brand.900">
        Search Tracers
      </Heading>
      <Box p={5} width={{ base: '100%' }}>
        <HStack mt={3} mb={1} spacing={8}>
          <SearchInput isDisabled={false} searchText={searchText} dataType="text" onChange={handleSearchTextChange} onClear={handleClearSearchText} />
          <FormControl id="sortBy" display="flex" alignItems="center">
            <FormLabel htmlFor="errorsOnly" mb="0" ml={2}>
              Errors Only
            </FormLabel>
            <Switch isChecked={isOnlyErrors} id="errorsOnly" name="errorsOnly" size="md" colorScheme="cyan" onChange={handleErrorsOnlyChange} />
          </FormControl>
        </HStack>
      </Box>
      {isError ? (
        <AlertComponent status="warning" title="Search Tracers Error" description={errorDescription?.data?.detail ?? 'Error fetching tracers'} />
      ) : (
        <SSDataTable
          loading={isLoading}
          data={tracerData?.items}
          total={tracerData?.total ?? 0}
          columns={tracerColumns}
          pagination={paginationState}
          pageCount={pageCount}
          onPaginationChange={onPaginationChange}
          sorting={sorting}
          onSortingChange={onSortingChange}
          onTableLoaded={onTableLoaded}
        />
      )}
    </Box>
  );
};

export default SearchTracer;
